// src/components/widgets/EmbeddedWidgetEditor.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Settings,
  Package,
  FileText,
  MessageSquare,
  Palette,
  Code,
  Copy,
  Check,
  Power,
  Edit3,
  Trash2,
  Plus,
  X,
  Monitor,
  Sun,
  Moon,
  ExternalLink,
  Layout,
  MousePointerClick,
  MessageCircle,
  Globe,
  BookOpen,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Smartphone
} from 'lucide-react';

// shadcn components
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';

import useWidgets from '../../hooks/useWidgets';
import { useSettings } from '../../hooks/useSettings';
import { cn } from '../../utils/cn';
import QuestionFormDialog from './QuestionFormDialog';
import WidgetPreview from './WidgetPreview';

const EmbeddedWidgetEditor = ({ widgetId }) => {
  const {
    selectedWidget,
    editing,
    startEditing,
    updateEditingDraft,
    updatePreviewState,
    stopEditing,
    toggleWidgetStatus,
    savingWidget,
    publishingWidget,
    publishWidget,
    generateEmbedCode,
    getEmbedCode,
    generatingEmbed
  } = useWidgets();

  const { services } = useSettings();

  const [activeTab, setActiveTab] = useState('configure');
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [copiedSection, setCopiedSection] = useState(null);
  const [expandedFields, setExpandedFields] = useState({});

  // Start editing session when widget is selected
  useEffect(() => {
    if (selectedWidget?.id) {
      startEditing(selectedWidget.id);
    }

    return () => {
      if (editing?.widgetId) {
        stopEditing();
      }
    };
  }, [selectedWidget?.id]);

  // Generate embed code on mount
  useEffect(() => {
    if (widgetId) {
      generateEmbedCode(widgetId);
    }
  }, [widgetId, generateEmbedCode]);

  const embedData = getEmbedCode(widgetId);

  // --- Handlers ---

  const handleFieldChange = (field, value) => {
    updateEditingDraft({ [field]: value });
  };

  const handleConfigChange = (field, value) => {
    if (!editing?.draft) return;
    updateEditingDraft({
      configuration: {
        ...editing.draft.configuration,
        [field]: value
      }
    });
  };

  const handleServiceToggle = (serviceId) => {
    if (!editing?.draft) return;
    const currentEnabled = editing.draft.configuration?.enabled_services || [];
    const newEnabled = currentEnabled.includes(serviceId)
      ? currentEnabled.filter(id => id !== serviceId)
      : [...currentEnabled, serviceId];
    handleConfigChange('enabled_services', newEnabled);
  };

  const handleFieldLabelChange = (fieldKey, newLabel) => {
    if (!editing?.draft) return;
    const currentLabels = editing.draft.configuration?.field_labels || {};
    handleConfigChange('field_labels', {
      ...currentLabels,
      [`${fieldKey}_label`]: newLabel
    });
  };

  const handleFieldPlaceholderChange = (fieldKey, newPlaceholder) => {
    if (!editing?.draft) return;
    const currentLabels = editing.draft.configuration?.field_labels || {};
    handleConfigChange('field_labels', {
      ...currentLabels,
      [`${fieldKey}_placeholder`]: newPlaceholder
    });
  };

  const handlePublish = () => {
    if (!selectedWidget) return;
    publishWidget(selectedWidget.id);
  };

  // Custom questions handlers
  const handleAddQuestion = () => {
    setEditingQuestion(null);
    setIsDialogOpen(true);
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setIsDialogOpen(true);
  };

  const handleSaveQuestion = (questionData) => {
    if (!editing?.draft) return;
    const currentQuestions = editing.draft.configuration?.custom_questions || [];
    if (editingQuestion) {
      const updatedQuestions = currentQuestions.map(q =>
        q.id === questionData.id ? questionData : q
      );
      handleConfigChange('custom_questions', updatedQuestions);
    } else {
      handleConfigChange('custom_questions', [...currentQuestions, questionData]);
    }
  };

  const handleDeleteQuestion = (questionId) => {
    if (!editing?.draft) return;
    if (confirm('Are you sure you want to delete this custom field?')) {
      const currentQuestions = editing.draft.configuration?.custom_questions || [];
      handleConfigChange('custom_questions', currentQuestions.filter(q => q.id !== questionId));
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text, section) => {
    navigator.clipboard.writeText(text);
    setCopiedSection(section);
    setTimeout(() => setCopiedSection(null), 2000);
  };

  // Toggle field expansion
  const toggleFieldExpanded = (fieldKey) => {
    setExpandedFields(prev => ({ ...prev, [fieldKey]: !prev[fieldKey] }));
  };

  // --- Helpers ---

  const getQuestionTypeLabel = (type) => {
    const labels = {
      text: 'Short Text',
      textarea: 'Long Text',
      select: 'Dropdown',
      radio: 'Radio',
      checkbox: 'Checkbox'
    };
    return labels[type] || type;
  };

  const formatOptions = (question) => {
    if (!question.options || question.options.length === 0) return '—';
    if (question.options.length <= 3) return question.options.join(', ');
    return `${question.options.slice(0, 2).join(', ')}... (${question.options.length} total)`;
  };

  const formatValidation = (question) => {
    if (!question.validation) return '—';
    const { min_length, max_length } = question.validation;
    if (!min_length && !max_length) return '—';
    if (min_length && max_length) return `${min_length}-${max_length} chars`;
    if (min_length) return `Min ${min_length} chars`;
    if (max_length) return `Max ${max_length} chars`;
    return '—';
  };

  const getStepLabel = (step) => {
    return step === 'service' ? 'Service' : 'Contact';
  };

  // Filter services
  const activeServices = services?.filter(service => service.active) || [];
  const enabledServicesCount = (editing.draft?.configuration?.enabled_services || []).length || activeServices.length;
  const customQuestionsCount = (editing.draft?.configuration?.custom_questions || []).length;

  // Theme options
  const themeOptions = [
    { value: 'auto', label: 'Auto', icon: Monitor },
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon }
  ];

  // Preset colors
  const presetColors = [
    '#007bff', '#28a745', '#dc3545', '#ffc107', '#6f42c1',
    '#fd7e14', '#20c997', '#e83e8c', '#6c757d', '#343a40'
  ];

  // Optional fields config
  const optionalFields = [
    {
      key: 'address',
      configKey: 'show_address',
      label: 'Customer Address',
      description: 'Collect and store service location address',
      defaultLabel: 'Service Address',
      defaultPlaceholder: 'Enter your address'
    },
    {
      key: 'notes',
      configKey: 'show_notes',
      label: 'Booking Notes',
      description: 'Allow customers to provide additional details',
      defaultVisible: true,
      defaultLabel: 'Additional Notes',
      defaultPlaceholder: 'Any additional details...'
    },
    {
      key: 'how_heard',
      configKey: 'show_how_heard',
      label: 'Referral Source',
      description: 'Track how customers found your business',
      defaultVisible: true,
      defaultLabel: 'How did you hear about us?',
      hasPlaceholder: false
    },
    {
      key: 'sms_consent',
      configKey: 'show_sms_consent',
      label: 'SMS Consent',
      description: 'Collect permission for SMS communications',
      defaultVisible: true,
      defaultLabel: 'I agree to receive SMS messages',
      hasPlaceholder: false
    }
  ];

  // Embed code variations
  const embedVariations = useMemo(() => {
    if (!embedData) return {};
    const baseUrl = embedData.preview_url;
    return {
      inline: `<!-- Inline Booking Widget -->\n<div style="position: relative; width: 100%; height: 0; padding-bottom: 75%;">\n  <iframe\n    src="${baseUrl}"\n    style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none; border-radius: 8px;"\n    allowfullscreen>\n  </iframe>\n</div>`,
      popup: `<!-- Popup Booking Button -->\n<a href="#" id="booking-widget-trigger" style="display: inline-block; padding: 12px 24px; background: #0066ff; color: white; text-decoration: none; border-radius: 6px; font-weight: 500;">\n  Book Appointment\n</a>\n\n<div id="booking-widget-modal" style="display: none; position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); z-index: 9999; align-items: center; justify-content: center;">\n  <div style="position: relative; width: 90%; max-width: 800px; height: 90%; max-height: 700px; background: white; border-radius: 12px; box-shadow: 0 10px 40px rgba(0,0,0,0.2);">\n    <button id="booking-widget-close" style="position: absolute; top: 12px; right: 12px; width: 32px; height: 32px; border: none; background: #f3f4f6; border-radius: 50%; cursor: pointer; font-size: 20px; z-index: 10000;">×</button>\n    <iframe src="${baseUrl}" style="width: 100%; height: 100%; border: none; border-radius: 12px;"></iframe>\n  </div>\n</div>\n\n<script>\n  (function() {\n    var trigger = document.getElementById('booking-widget-trigger');\n    var modal = document.getElementById('booking-widget-modal');\n    var close = document.getElementById('booking-widget-close');\n    trigger.addEventListener('click', function(e) { e.preventDefault(); modal.style.display = 'flex'; });\n    close.addEventListener('click', function() { modal.style.display = 'none'; });\n    modal.addEventListener('click', function(e) { if (e.target === modal) modal.style.display = 'none'; });\n  })();\n</script>`,
      badge: `<!-- Floating Booking Badge -->\n<div id="booking-widget-badge" style="position: fixed; bottom: 24px; right: 24px; z-index: 9998;">\n  <button id="booking-badge-trigger" style="padding: 14px 20px; background: #0066ff; color: white; border: none; border-radius: 50px; font-weight: 500; cursor: pointer; box-shadow: 0 4px 12px rgba(0,102,255,0.4); display: flex; align-items: center; gap: 8px;">\n    Book Now\n  </button>\n</div>\n\n<div id="booking-badge-modal" style="display: none; position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); z-index: 9999; align-items: center; justify-content: center;">\n  <div style="position: relative; width: 90%; max-width: 800px; height: 90%; max-height: 700px; background: white; border-radius: 12px; box-shadow: 0 10px 40px rgba(0,0,0,0.2);">\n    <button id="booking-badge-close" style="position: absolute; top: 12px; right: 12px; width: 32px; height: 32px; border: none; background: #f3f4f6; border-radius: 50%; cursor: pointer; font-size: 20px; z-index: 10000;">×</button>\n    <iframe src="${baseUrl}" style="width: 100%; height: 100%; border: none; border-radius: 12px;"></iframe>\n  </div>\n</div>\n\n<script>\n  (function() {\n    var trigger = document.getElementById('booking-badge-trigger');\n    var modal = document.getElementById('booking-badge-modal');\n    var close = document.getElementById('booking-badge-close');\n    trigger.addEventListener('click', function() { modal.style.display = 'flex'; });\n    close.addEventListener('click', function() { modal.style.display = 'none'; });\n    modal.addEventListener('click', function(e) { if (e.target === modal) modal.style.display = 'none'; });\n  })();\n</script>`
    };
  }, [embedData]);

  const platformInstructions = [
    {
      platform: 'WordPress',
      icon: Globe,
      steps: ['Edit the page where you want to add the booking widget', 'Add a new "Custom HTML" or "HTML" block', 'Paste the iframe code into the HTML block', 'Save and publish your page']
    },
    {
      platform: 'Squarespace',
      icon: Globe,
      steps: ['Edit the page in Squarespace', 'Add a new "Code" block', 'Paste the iframe code', 'Save and publish']
    },
    {
      platform: 'Wix',
      icon: Globe,
      steps: ['Go to your Wix editor', 'Click "+ Add" and select "Embed Code"', 'Choose "HTML iframe"', 'Paste the code and adjust size', 'Save and publish']
    },
    {
      platform: 'Webflow',
      icon: Globe,
      steps: ['Drag an "Embed" element to your page', 'Paste the iframe code', 'Set width to 100% and height to 600px', 'Publish your site']
    },
    {
      platform: 'Custom HTML',
      icon: Code,
      steps: ['Open your HTML file', 'Find where you want the widget', 'Paste the iframe code', 'Save and upload to your server']
    }
  ];

  if (!selectedWidget) {
    return (
      <div className="h-full flex items-center justify-center bg-muted/20">
        <div className="text-center p-8">
          <Settings className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Select a Widget</h3>
          <p className="text-muted-foreground text-sm">
            Choose a widget from the list to configure
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
        {/* Tab Bar */}
        <div className="flex-shrink-0 flex items-center justify-between border-b bg-card px-4 py-2">
          <div className="flex items-center gap-3">
            <TabsList>
              <TabsTrigger value="configure" className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Configure
              </TabsTrigger>
              <TabsTrigger value="preview" className="flex items-center gap-2">
                <Monitor className="w-4 h-4" />
                Preview
              </TabsTrigger>
            </TabsList>

            {/* Device Toggle - Only on Preview tab */}
            {activeTab === 'preview' && (
              <div className="flex rounded-lg border p-1">
                <Button
                  variant={previewDevice === 'desktop' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setPreviewDevice('desktop')}
                  className="h-7 w-7 p-0"
                  title="Desktop view"
                >
                  <Monitor className="w-4 h-4" />
                </Button>
                <Button
                  variant={previewDevice === 'mobile' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setPreviewDevice('mobile')}
                  className="h-7 w-7 p-0"
                  title="Mobile view"
                >
                  <Smartphone className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Right Side: Save/Publish Status */}
          <div className="flex items-center gap-3">
            {savingWidget && (
              <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                <div className="w-3 h-3 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
                Saving...
              </span>
            )}
            {selectedWidget?.has_unpublished_changes && !savingWidget && (
              <span className="text-xs text-muted-foreground">
                Unpublished changes
              </span>
            )}
            {selectedWidget?.has_unpublished_changes && (
              <Button
                onClick={handlePublish}
                disabled={publishingWidget || savingWidget}
                size="sm"
                className="h-7"
              >
                {publishingWidget ? (
                  <>
                    <div className="w-3 h-3 border-2 border-background/30 border-t-background rounded-full animate-spin mr-1" />
                    Publishing...
                  </>
                ) : (
                  'Publish'
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Configure Tab */}
        <TabsContent value="configure" className="flex-1 overflow-hidden mt-0">
          <div className="h-full overflow-y-auto bg-gradient-to-tl from-[#0a0a0a] from-0% via-[#0b0b0f] via-20% via-[#0c0c18] via-40% via-[#0b0b12] via-60% via-[#0b0b0e] via-80% to-[#0a0a0a] to-100%">
            <div className="pt-20 pl-36 pr-36 pb-20 space-y-10">
              {/* Header */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <h1 className="text-4xl font-bold text-foreground">
                    {selectedWidget.widget_name}
                  </h1>
                  <Badge variant="secondary" className="text-sm">Embedded</Badge>
                  <Badge variant={selectedWidget.is_active ? "default" : "secondary"} className="text-sm">
                    {selectedWidget.is_active ? "Active" : "Inactive"}
                  </Badge>
                </div>
                <div className="h-0.5 w-full bg-gradient-to-r from-primary/50 to-transparent" />
              </div>

              {/* Widget Status Section */}
              <div className="space-y-5">
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                    <Power className="w-5 h-5" />
                    Widget Status
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                    Control whether this widget is available for use. Inactive widgets can still be configured but won't accept bookings.
                  </p>
                </div>

                <div className="flex items-center justify-between p-4 bg-muted/10 rounded-lg border border-border/50">
                  <div>
                    <Label className="text-base font-medium">Active Status</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Make this widget available for public use
                    </p>
                  </div>
                  <Switch
                    checked={selectedWidget.is_active}
                    onCheckedChange={(checked) => toggleWidgetStatus(selectedWidget.id, checked)}
                    disabled={savingWidget}
                  />
                </div>
              </div>

              <Separator />

              {/* Appearance Section */}
              <div className="space-y-5">
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                    <Palette className="w-5 h-5" />
                    Appearance
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                    Customize the look and feel of your booking widget to match your brand.
                  </p>
                </div>

                {/* Primary Color */}
                <div className="space-y-3">
                  <Label className="text-sm font-semibold">Primary Color</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="color"
                      value={editing.draft?.primary_color || '#007bff'}
                      onChange={(e) => handleFieldChange('primary_color', e.target.value)}
                      className="w-12 h-9 p-1 cursor-pointer"
                    />
                    <Input
                      value={editing.draft?.primary_color || '#007bff'}
                      onChange={(e) => handleFieldChange('primary_color', e.target.value)}
                      placeholder="#007bff"
                      className="w-32 h-9"
                    />
                  </div>
                  <div className="flex gap-2">
                    {presetColors.map(color => (
                      <button
                        key={color}
                        onClick={() => handleFieldChange('primary_color', color)}
                        className={cn(
                          "w-7 h-7 rounded border-2 transition-all hover:scale-110",
                          editing.draft?.primary_color === color ? "border-primary scale-110" : "border-border/50"
                        )}
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                </div>

                {/* Theme */}
                <div className="space-y-3">
                  <Label className="text-sm font-semibold">Theme</Label>
                  <RadioGroup
                    value={editing.draft?.theme || 'auto'}
                    onValueChange={(value) => handleFieldChange('theme', value)}
                    className="space-y-2"
                  >
                    {themeOptions.map(option => {
                      const Icon = option.icon;
                      return (
                        <div key={option.value} className="flex items-center space-x-2">
                          <RadioGroupItem value={option.value} id={`theme-${option.value}`} />
                          <Label htmlFor={`theme-${option.value}`} className="flex items-center gap-2 cursor-pointer text-sm font-normal">
                            <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                            {option.label}
                          </Label>
                        </div>
                      );
                    })}
                  </RadioGroup>
                </div>

                {/* Border Radius */}
                <div className="space-y-3">
                  <Label className="text-sm font-semibold">Border Radius</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      max="24"
                      value={editing.draft?.border_radius ?? 8}
                      onChange={(e) => handleFieldChange('border_radius', parseInt(e.target.value) || 0)}
                      className="w-24 h-9"
                    />
                    <span className="text-sm text-muted-foreground">px</span>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Available Services Section */}
              <div className="space-y-5">
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                    <Package className="w-5 h-5" />
                    Available Services
                    {enabledServicesCount > 0 && (
                      <span className="ml-2 px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full font-normal">
                        {enabledServicesCount}
                      </span>
                    )}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                    Select which services customers can book through this widget. If no services are selected, all active services will be available.
                  </p>
                </div>

                <div className="space-y-3">
                  {activeServices.length === 0 ? (
                    <div className="text-center py-12 bg-muted/10 rounded-lg border border-border/50">
                      <Package className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                      <p className="text-sm text-muted-foreground">No active services available</p>
                    </div>
                  ) : (
                    activeServices.map((service) => {
                      const isEnabled = (editing.draft?.configuration?.enabled_services || []).includes(service.id) ||
                                       (editing.draft?.configuration?.enabled_services || []).length === 0;
                      return (
                        <div
                          key={service.id}
                          className="flex items-start gap-3 p-4 bg-muted/10 rounded-lg border border-border/50 hover:bg-muted/20 transition-colors cursor-pointer"
                          onClick={() => handleServiceToggle(service.id)}
                        >
                          <input
                            type="checkbox"
                            checked={isEnabled}
                            onChange={() => handleServiceToggle(service.id)}
                            className="mt-1 rounded border-gray-300 cursor-pointer"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: service.color_code }} />
                              <p className="font-medium">{service.name}</p>
                            </div>
                            {service.description && (
                              <p className="text-sm text-muted-foreground mt-1">{service.description}</p>
                            )}
                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                              {service.base_price && <span>${service.base_price}</span>}
                              {service.base_duration && <span>{service.base_duration} min</span>}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <Separator />

              {/* Form Fields Section */}
              <div className="space-y-5">
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                    <FileText className="w-5 h-5" />
                    Form Fields
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                    Configure which fields appear on the booking form and customize their labels and placeholders.
                  </p>
                </div>

                {/* Required Fields Info */}
                <div className="p-4 bg-muted/10 rounded-lg border border-border/50">
                  <p className="text-sm font-semibold mb-2">Required Fields (Always Collected)</p>
                  <div className="space-y-2">
                    {[
                      { key: 'name', label: 'Customer Name', defaultPlaceholder: 'Enter your full name' },
                      { key: 'email', label: 'Email Address', defaultPlaceholder: 'your@email.com' },
                      { key: 'phone', label: 'Phone Number', defaultPlaceholder: '(555) 000-0000' }
                    ].map(field => {
                      const currentLabel = editing.draft?.configuration?.field_labels?.[`${field.key}_label`];
                      const currentPlaceholder = editing.draft?.configuration?.field_labels?.[`${field.key}_placeholder`];
                      const isExpanded = expandedFields[field.key];

                      return (
                        <Collapsible key={field.key} open={isExpanded} onOpenChange={() => toggleFieldExpanded(field.key)}>
                          <CollapsibleTrigger className="flex items-center justify-between w-full p-3 rounded-md hover:bg-muted/10 transition-colors">
                            <div className="flex items-center gap-2">
                              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                              <span className="text-sm font-medium">{field.label}</span>
                              {(currentLabel || currentPlaceholder) && (
                                <Badge variant="outline" className="text-xs">Customized</Badge>
                              )}
                            </div>
                            {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                          </CollapsibleTrigger>
                          <CollapsibleContent className="pl-9 pr-3 pb-3 space-y-3">
                            <div className="space-y-2">
                              <Label className="text-xs text-muted-foreground">Label</Label>
                              <Input
                                value={currentLabel || ''}
                                onChange={(e) => handleFieldLabelChange(field.key, e.target.value)}
                                placeholder={field.label}
                                className="h-8 text-sm"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-xs text-muted-foreground">Placeholder</Label>
                              <Input
                                value={currentPlaceholder || ''}
                                onChange={(e) => handleFieldPlaceholderChange(field.key, e.target.value)}
                                placeholder={field.defaultPlaceholder}
                                className="h-8 text-sm"
                              />
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      );
                    })}
                  </div>
                </div>

                {/* Optional Data Fields */}
                <div className="space-y-3">
                  <Label className="text-sm font-semibold">Optional Fields</Label>

                  {optionalFields.map(field => {
                    const isVisible = field.defaultVisible
                      ? editing.draft?.configuration?.[field.configKey] !== false
                      : editing.draft?.configuration?.[field.configKey] || false;
                    const currentLabel = editing.draft?.configuration?.field_labels?.[`${field.key}_label`];
                    const currentPlaceholder = editing.draft?.configuration?.field_labels?.[`${field.key}_placeholder`];
                    const isExpanded = expandedFields[field.key];

                    return (
                      <div key={field.key} className="bg-muted/10 rounded-lg border border-border/50">
                        <div className="flex items-center justify-between p-4">
                          <div className="flex-1">
                            <Label className="font-medium">{field.label}</Label>
                            <p className="text-xs text-muted-foreground mt-1">{field.description}</p>
                          </div>
                          <Switch
                            checked={isVisible}
                            onCheckedChange={() => {
                              const currentValue = field.defaultVisible
                                ? editing.draft?.configuration?.[field.configKey] !== false
                                : editing.draft?.configuration?.[field.configKey] || false;
                              handleConfigChange(field.configKey, !currentValue);
                            }}
                          />
                        </div>

                        {/* Label/Placeholder customization when visible */}
                        {isVisible && field.hasPlaceholder !== false && (
                          <Collapsible open={isExpanded} onOpenChange={() => toggleFieldExpanded(field.key)}>
                            <CollapsibleTrigger className="flex items-center gap-2 px-4 pb-2 text-xs text-muted-foreground hover:text-foreground transition-colors">
                              {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                              Customize label & placeholder
                              {(currentLabel || currentPlaceholder) && (
                                <Badge variant="outline" className="text-[10px] px-1 py-0">Customized</Badge>
                              )}
                            </CollapsibleTrigger>
                            <CollapsibleContent className="px-4 pb-4 space-y-3">
                              <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Label</Label>
                                <Input
                                  value={currentLabel || ''}
                                  onChange={(e) => handleFieldLabelChange(field.key, e.target.value)}
                                  placeholder={field.defaultLabel}
                                  className="h-8 text-sm"
                                />
                              </div>
                              {field.hasPlaceholder !== false && (
                                <div className="space-y-2">
                                  <Label className="text-xs text-muted-foreground">Placeholder</Label>
                                  <Input
                                    value={currentPlaceholder || ''}
                                    onChange={(e) => handleFieldPlaceholderChange(field.key, e.target.value)}
                                    placeholder={field.defaultPlaceholder}
                                    className="h-8 text-sm"
                                  />
                                </div>
                              )}
                            </CollapsibleContent>
                          </Collapsible>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <Separator />

              {/* Custom Data Fields Section */}
              <div className="space-y-5">
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                    <MessageSquare className="w-5 h-5" />
                    Custom Data Fields
                    {customQuestionsCount > 0 && (
                      <span className="ml-2 px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full font-normal">
                        {customQuestionsCount}
                      </span>
                    )}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                    Add custom fields to collect additional information from customers during booking.
                  </p>
                </div>

                <div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Label</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Options</TableHead>
                        <TableHead className="w-24">Required</TableHead>
                        <TableHead>Validation</TableHead>
                        <TableHead className="w-24">Step</TableHead>
                        <TableHead className="w-20 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(editing.draft?.configuration?.custom_questions || []).map((question) => (
                        <TableRow key={question.id} className="group">
                          <TableCell className="font-medium">{question.label}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {getQuestionTypeLabel(question.type)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatOptions(question)}
                          </TableCell>
                          <TableCell className="text-sm">
                            {question.required ? (
                              <Check className="w-4 h-4 text-emerald-500" />
                            ) : (
                              <X className="w-4 h-4 text-muted-foreground" />
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatValidation(question)}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={question.show_on_step === 'service' ? 'default' : 'secondary'}
                              className="text-xs"
                            >
                              {getStepLabel(question.show_on_step)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditQuestion(question)}
                                className="h-7 w-7 p-0 hover:bg-zinc-700/50"
                              >
                                <Edit3 className="w-3 h-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteQuestion(question.id)}
                                className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}

                      {customQuestionsCount === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-12">
                            <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                            <p className="text-sm text-muted-foreground mb-4">No custom fields added</p>
                            <Button size="sm" variant="outline" onClick={handleAddQuestion}>
                              <Plus className="w-4 h-4 mr-2" />
                              Add Custom Field
                            </Button>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>

                  {customQuestionsCount > 0 && (
                    <div className="mt-4">
                      <Button size="sm" variant="outline" onClick={handleAddQuestion}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Custom Field
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              <Separator />

              {/* Success Message Section */}
              <div className="space-y-5">
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                    <CheckCircle2 className="w-5 h-5" />
                    Success Message
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                    Customize the message shown to customers after they successfully book an appointment.
                  </p>
                </div>

                <div className="space-y-2">
                  <Textarea
                    value={editing.draft?.configuration?.success_message || ''}
                    onChange={(e) => handleConfigChange('success_message', e.target.value)}
                    placeholder="Thank you for booking! We'll send you a confirmation email shortly."
                    rows={3}
                    className="resize-none"
                  />
                  <p className="text-xs text-muted-foreground">
                    Leave empty to use the default success message.
                  </p>
                </div>
              </div>

              <Separator />

              {/* Embed Code Section */}
              <div className="space-y-5">
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                    <Code className="w-5 h-5" />
                    Embed Code
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                    Add this widget to your website by copying the embed code below.
                  </p>
                </div>

                {generatingEmbed ? (
                  <div className="flex items-center justify-center py-12 bg-muted/10 rounded-lg border border-border/50">
                    <div className="text-center">
                      <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4" />
                      <p className="text-muted-foreground text-sm">Generating embed code...</p>
                    </div>
                  </div>
                ) : embedData ? (
                  <div className="space-y-4">
                    {/* Widget URL */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Widget URL</Label>
                      <div className="flex gap-2">
                        <Input
                          value={embedData.preview_url}
                          readOnly
                          className="font-mono text-sm bg-zinc-900 border border-zinc-700/50"
                        />
                        <Button
                          size="icon"
                          variant="outline"
                          onClick={() => copyToClipboard(embedData.preview_url, 'url')}
                          className="shrink-0"
                        >
                          {copiedSection === 'url' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </Button>
                        <Button variant="outline" size="icon" asChild className="shrink-0">
                          <a href={embedData.preview_url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </Button>
                      </div>
                    </div>

                    {/* Embed Types */}
                    <div className="space-y-4">
                      {/* Inline Widget */}
                      <div className="bg-muted/10 rounded-lg border border-border/50 p-4 space-y-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className="p-2 rounded-lg bg-primary/10 text-primary">
                              <Layout className="w-4 h-4" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <p className="text-sm font-semibold">Inline Widget</p>
                                <Badge variant="secondary" className="text-xs">Most Common</Badge>
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Widget appears directly on your page, always visible.
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyToClipboard(embedVariations.inline, 'inline')}
                          >
                            {copiedSection === 'inline' ? (
                              <><Check className="w-3 h-3 mr-1" /> Copied</>
                            ) : (
                              <><Copy className="w-3 h-3 mr-1" /> Copy</>
                            )}
                          </Button>
                        </div>
                        <Textarea
                          value={embedVariations.inline}
                          readOnly
                          className="font-mono text-xs resize-none h-20 bg-zinc-900 border-zinc-700/50"
                        />
                      </div>

                      {/* Popup Button */}
                      <div className="bg-muted/10 rounded-lg border border-border/50 p-4 space-y-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className="p-2 rounded-lg bg-primary/10 text-primary">
                              <MousePointerClick className="w-4 h-4" />
                            </div>
                            <div>
                              <p className="text-sm font-semibold">Popup Button</p>
                              <p className="text-xs text-muted-foreground">
                                Opens the widget in a modal when clicked.
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyToClipboard(embedVariations.popup, 'popup')}
                          >
                            {copiedSection === 'popup' ? (
                              <><Check className="w-3 h-3 mr-1" /> Copied</>
                            ) : (
                              <><Copy className="w-3 h-3 mr-1" /> Copy</>
                            )}
                          </Button>
                        </div>
                        <Textarea
                          value={embedVariations.popup}
                          readOnly
                          className="font-mono text-xs resize-none h-24 bg-zinc-900 border-zinc-700/50"
                        />
                      </div>

                      {/* Floating Badge */}
                      <div className="bg-muted/10 rounded-lg border border-border/50 p-4 space-y-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className="p-2 rounded-lg bg-primary/10 text-primary">
                              <MessageCircle className="w-4 h-4" />
                            </div>
                            <div>
                              <p className="text-sm font-semibold">Floating Badge</p>
                              <p className="text-xs text-muted-foreground">
                                Persistent floating button in the bottom-right corner.
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyToClipboard(embedVariations.badge, 'badge')}
                          >
                            {copiedSection === 'badge' ? (
                              <><Check className="w-3 h-3 mr-1" /> Copied</>
                            ) : (
                              <><Copy className="w-3 h-3 mr-1" /> Copy</>
                            )}
                          </Button>
                        </div>
                        <Textarea
                          value={embedVariations.badge}
                          readOnly
                          className="font-mono text-xs resize-none h-24 bg-zinc-900 border-zinc-700/50"
                        />
                      </div>
                    </div>

                    {/* Platform Instructions */}
                    <Collapsible>
                      <CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                        <BookOpen className="w-4 h-4" />
                        Platform-specific instructions
                        <ChevronRight className="w-4 h-4" />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {platformInstructions.map((platform, index) => {
                          const Icon = platform.icon;
                          return (
                            <div key={index} className="bg-muted/10 rounded-lg border border-border/50 p-4">
                              <h4 className="font-medium flex items-center gap-2 mb-3">
                                <Icon className="w-4 h-4" />
                                {platform.platform}
                              </h4>
                              <ol className="space-y-1.5">
                                {platform.steps.map((step, stepIndex) => (
                                  <li key={stepIndex} className="text-sm flex">
                                    <span className="text-primary font-medium mr-2 flex-shrink-0">{stepIndex + 1}.</span>
                                    <span className="text-muted-foreground">{step}</span>
                                  </li>
                                ))}
                              </ol>
                            </div>
                          );
                        })}
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                ) : (
                  <div className="text-center py-12 bg-muted/10 rounded-lg border border-border/50">
                    <Code className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">
                      Embed code will appear here once generated.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Preview Tab */}
        <TabsContent value="preview" className="flex-1 overflow-hidden mt-0">
          {editing?.draft && (
            <WidgetPreview
              formData={editing.draft}
              previewState={editing.previewState}
              onPreviewStateChange={updatePreviewState}
              onConfigChange={() => {}}
              configurationMode={false}
              showControlBar={false}
              previewDevice={previewDevice}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Question Form Dialog */}
      <QuestionFormDialog
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          setEditingQuestion(null);
        }}
        onSave={handleSaveQuestion}
        initialData={editingQuestion}
      />
    </div>
  );
};

export default EmbeddedWidgetEditor;
